import { Component } from '@theme/component';

class LocationVariantSelector extends Component {
  /** @type {string | null} */
  #currentPricebook = null;

  selectors = {
    dataProductId: '[data-product-id]',
    priceContainer: '.price__container',
    priceSelectStore: '[data-price-select-store]',
    priceSelectStoreButton: '[data-price-select-store-button]',
    addToCartWrapper: '.add-to-cart__wrapper',
    /** @param {string} productId */
    productVariantsScriptByProductId: (productId) => `script[id^="ProductVariantsJSON-${productId}"]`,
    productVariantsScript: 'script[id^="ProductVariantsJSON-"]',
    /** @param {string} productId */
    productFormComponent: (productId) => `product-form-component[data-product-id="${productId}"]`,
    formBuyButtons: 'form[id^="BuyButtons-ProductForm-"]',
    formProductForm: 'form[id^="product-form-"]',
    productFormComponentForm: 'product-form-component form',
    inputVariantId: 'input[name="id"]',
    /** @param {string} productId */
    variantPicker: (productId) => `variant-picker[data-product-id="${productId}"]`,
    priceBlock: '[id^="price-"]',
    priceGroup: '.price-group',
    variantSelectsSection: 'variant-selects[data-section]',
    productInfoSection: 'product-info[data-section]',
    attributeDataSection: 'data-section',
    attributeDataProductUrl: 'data-product-url'
  };

  connectedCallback() {
    super.connectedCallback();

    this.#setupSelectStoreButton();
    this.#applyVariantFromPreferredStore();

    document.addEventListener('location:changed', this.#handleLocationChange);
    document.addEventListener('pricebook:changed', this.#handlePricebookChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('location:changed', this.#handleLocationChange);
    document.removeEventListener('pricebook:changed', this.#handlePricebookChange);
  }

  #applyVariantFromPreferredStore() {
    const tryApply = () => {
      this.#initializeFromPreferredStore().then((applied) => {
        if (applied) return;
        this.#retryApplyVariant = this.#retryApplyVariant ?? 0;
        if (this.#retryApplyVariant < 3) {
          this.#retryApplyVariant += 1;
          const delay = this.#retryApplyVariant === 1 ? 150 : this.#retryApplyVariant === 2 ? 500 : 1000;
          setTimeout(tryApply, delay);
        } else {
          this.#showSelectStoreState();
        }
      });
    };

    tryApply();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(tryApply, 50));
    }
  }

  /** @type {number | undefined} */
  #retryApplyVariant;

  /**
   * @returns {Promise<boolean>}
   */
  async #initializeFromPreferredStore() {
    try {
      const win = /** @type {any} */ (window);
      if (!win || typeof win.getPreferredStore !== 'function') {
        this.#currentPricebook = null;
        return false;
      }

      const store = await win.getPreferredStore();
      if (!store) {
        this.#currentPricebook = null;
        this.#showSelectStoreState();
        return true;
      }

      const pricebook =
        store.priceBook ||
        store.pricebook ||
        store.metafields?.custom?.price_book?.value ||
        null;

      if (!pricebook) {
        this.#currentPricebook = null;
        this.#resetToDefaultVariant();
        this.#showPriceState();
        return true;
      }

      this.#currentPricebook = pricebook;
      this.#showPriceState();
      this.#selectVariantByPricebook(pricebook);
      return true;
    } catch (e) {
      this.#currentPricebook = null;
      this.#showSelectStoreState();
      return true;
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleLocationChange = (event) => {
    const pricebook = event?.detail?.pricebook ?? null;
    const store = event?.detail?.store ?? null;

    const hasStoreSelected = store !== null;
    const { selectStoreBlock } = this.#getPriceElements();
    const isSelectStoreBlockVisible = selectStoreBlock && !selectStoreBlock.classList.contains('hidden');

    if (pricebook === this.#currentPricebook) {
      if (hasStoreSelected && !isSelectStoreBlockVisible) {
        return;
      }
      if (!hasStoreSelected && isSelectStoreBlockVisible) {
        return;
      }
    }

    this.#currentPricebook = pricebook || null;
    if (!pricebook) {
      if (store) {
        this.#resetToDefaultVariant();
        this.#showPriceState();
      } else {
        this.#showSelectStoreState();
      }
      return;
    }
    this.#showPriceState();
    this.#selectVariantByPricebook(pricebook);
  };

  /**
   * @param {CustomEvent} event
   */
  #handlePricebookChange = (event) => {
    const pricebook = event?.detail?.pricebook ?? null;
    if (pricebook === this.#currentPricebook) return;

    this.#currentPricebook = pricebook || null;
    if (!pricebook) {
      this.#showSelectStoreState();
      return;
    }
    this.#showPriceState();
    this.#selectVariantByPricebook(pricebook);
  };

  #getPriceElements() {
    /** @type {HTMLElement | null} */
    const priceGroup =
      document.querySelector(this.selectors.priceBlock) ||
      document.querySelector(this.selectors.priceGroup);

    /** @type {HTMLElement | null} */
    const priceContainer =
      priceGroup?.querySelector(this.selectors.priceContainer) ||
      document.querySelector(this.selectors.priceContainer);

    /** @type {HTMLElement | null} */
    const selectStoreBlock =
      priceGroup?.querySelector(this.selectors.priceSelectStore) ||
      document.querySelector(this.selectors.priceSelectStore);

    return { priceContainer, selectStoreBlock };
  }

  #showSelectStoreState() {
    if (!this.#hasPriceBookOption()) return;

    const { priceContainer, selectStoreBlock } = this.#getPriceElements();

    if (priceContainer instanceof HTMLElement) {
      priceContainer.classList.add('hidden');
    }

    const cashbackBadges = document.querySelector('.cashback-badges');
    if (cashbackBadges instanceof HTMLElement) {
      cashbackBadges.classList.add('hidden');
    }

    const atcWrapper = document.querySelector(this.selectors.addToCartWrapper);
    if (atcWrapper instanceof HTMLElement) {
      atcWrapper.classList.add('hidden');
    }

    if (selectStoreBlock instanceof HTMLElement) {
      selectStoreBlock.classList.remove('hidden');
    }

    this.#removeInitialLoadingClass();
  }

  #showPriceState() {
    const { priceContainer, selectStoreBlock } = this.#getPriceElements();

    if (priceContainer instanceof HTMLElement) {
      priceContainer.classList.remove('hidden');
    }

    const cashbackBadges = document.querySelector('.cashback-badges');
    if (cashbackBadges instanceof HTMLElement) {
      cashbackBadges.classList.remove('hidden');
    }

    const atcWrapper = document.querySelector(this.selectors.addToCartWrapper);
    if (atcWrapper instanceof HTMLElement) {
      atcWrapper.classList.remove('hidden');
    }

    if (selectStoreBlock instanceof HTMLElement && this.#hasPriceBookOption()) {
      selectStoreBlock.classList.add('hidden');
    }
  }

  /**
   * Whether current product has "Price Book" option.
   * @returns {boolean}
   */
  #hasPriceBookOption() {
    return this.dataset.hasPriceBookOption === 'true';
  }

  #setupSelectStoreButton() {
    const { selectStoreBlock } = this.#getPriceElements();
    const button =
      selectStoreBlock?.querySelector(this.selectors.priceSelectStoreButton) || null;

    if (button instanceof HTMLButtonElement) {
      button.addEventListener('click', this.#handleSelectStoreClick);
    }
  }

  #handleSelectStoreClick = () => {
    this.#openSetStoreModal();
  };

  #openSetStoreModal() {
    try {
      const components = document.querySelectorAll('store-locator-component');

      for (const comp of components) {
        if (!(comp instanceof HTMLElement)) continue;
        const isVisible = comp.getClientRects().length > 0;
        if (!isVisible) continue;

        /** @type {any} */
        const instance =
          comp.shadowRoot?.querySelector('store-locator-component') || comp;

        if (instance && typeof instance.openSetStoreModal === 'function') {
          instance.openSetStoreModal();
          return;
        }
      }

      /** @type {HTMLElement | null} */
      const modal = document.querySelector('#set-store-modal');
      if (!modal) return;

      /** @type {any} */
      const dialogComponent = modal.querySelector('dialog-component');
      if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
        dialogComponent.showDialog();
        return;
      }

      const dialogEl = modal.querySelector('dialog');
      if (dialogEl && typeof dialogEl.showModal === 'function') {
        dialogEl.showModal();
      }
    } catch {
    }
  }

  #resetToDefaultVariant() {
    const productData = this.#getProductData();
    if (!productData) {
      this.#removeInitialLoadingClass();
      return;
    }

    // @ts-ignore
    const defaultVariant = productData.selected_variant || productData.variants?.[0];
    if (!defaultVariant?.id) {
      this.#removeInitialLoadingClass();
      return;
    }

    const productId = this.#getCurrentProductId();
    this.#syncFormVariantId(defaultVariant.id, productId);
    this.#updatePriceBlockByVariant(defaultVariant.id, productId);
  }

  /**
   * Get current product id from closest container if possible.
   * @returns {string | null}
   */
  #getCurrentProductId() {
    const container =
      this.closest(this.selectors.dataProductId) ||
      document.querySelector(this.selectors.dataProductId);
    const id = container?.getAttribute('data-product-id');
    return id || null;
  }

  /**
   * Get product variants data from ProductVariantsJSON script tag.
   * @returns {{ variants?: any[] } | null}
   */
  #getProductData() {
    const productId = this.#getCurrentProductId();

    /** @type {HTMLScriptElement | null} */
    let script = null;

    if (productId) {
      script = document.querySelector(this.selectors.productVariantsScriptByProductId(productId));
    }

    if (!script) {
      script = document.querySelector(this.selectors.productVariantsScript);
    }

    if (!script) return null;

    try {
      const text = script.textContent || script.innerText || '';
      return JSON.parse(text);
    } catch (e) {
      console.warn('LocationVariantSelector: could not parse ProductVariantsJSON', e);
      return null;
    }
  }

  /**
   * Normalize pricebook string for comparison.
   * @param {string} value
   * @returns {string}
   */
  #normalizePricebook(value) {
    return String(value).trim().toLowerCase();
  }

  /**
   * Select variant whose metafield custom.price_book matches given pricebook.
   * @param {string} pricebook
   */
  #selectVariantByPricebook(pricebook) {
    const productData = this.#getProductData();
    if (!productData?.variants?.length) {
      this.#removeInitialLoadingClass();
      return;
    }

    const targetPricebook = this.#normalizePricebook(pricebook);

    const matchingVariant = productData.variants.find((variant) => {
      const variantPricebook =
        variant?.metafields?.custom?.price_book?.value ??
        variant?.metafields?.custom?.price_book;
      if (!variantPricebook) return false;
      return (
        this.#normalizePricebook(String(variantPricebook)) === targetPricebook &&
        variant.available !== false
      );
    });

    if (!matchingVariant) {
      this.#removeInitialLoadingClass();
      return;
    }

    const productId = this.#getCurrentProductId();

    this.#syncFormVariantId(matchingVariant.id, productId);

    this.#updatePriceBlockByVariant(matchingVariant.id, productId);
  }

  /**
   * @param {number} variantId
   * @param {string | null} productId
   */
  #syncFormVariantId(variantId, productId) {
    let form = null;
    if (productId) {
      const wrap = document.querySelector(this.selectors.productFormComponent(productId));
      form = wrap?.querySelector('form');
    }
    if (!form) {
      form =
        document.querySelector(this.selectors.formBuyButtons) ||
        document.querySelector(this.selectors.formProductForm) ||
        document.querySelector(this.selectors.productFormComponentForm);
    }
    const value = String(variantId);
    form?.querySelectorAll(this.selectors.inputVariantId).forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = value;
    });
  }

  /**
   * Fetch main product section HTML for the given variant and replace the price block on the page.
   * @param {number} variantId
   * @param {string | null} productId
   */
  #updatePriceBlockByVariant(variantId, productId) {
    const sectionId = this.#getMainProductSectionId();
    if (!sectionId) {
      this.#removeInitialLoadingClass();
      return;
    }

    const productUrl =
      (productId && document.querySelector(this.selectors.variantPicker(productId))?.getAttribute(this.selectors.attributeDataProductUrl)) ||
      (productId && document.querySelector(this.selectors.productFormComponent(productId))?.getAttribute(this.selectors.attributeDataProductUrl)) ||
      (typeof window !== 'undefined' && window.location?.pathname?.startsWith?.('/products/') ? window.location.pathname : null);
    if (!productUrl) {
      this.#removeInitialLoadingClass();
      return;
    }

    const baseUrl = String(productUrl).split('?')[0];
    const url = `${baseUrl}?variant=${variantId}&section_id=${sectionId}`;

    const currentPriceBlock = document.querySelector(this.selectors.priceBlock) || document.querySelector(this.selectors.priceGroup);
    if (currentPriceBlock) {
      currentPriceBlock.classList.add('price--loading');
      currentPriceBlock.querySelector('.price')?.classList.add('price--loading');
    }

    fetch(url)
      .then((res) => res.text())
      .then((htmlText) => {
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const newPriceBlock = doc.querySelector(this.selectors.priceBlock) || doc.querySelector(this.selectors.priceGroup);
        if (newPriceBlock && currentPriceBlock) {
          currentPriceBlock.innerHTML = newPriceBlock.innerHTML;
        }
      })
      .catch((err) => {
        console.error('LocationVariantSelector: failed to update price block', err);
      })
      .finally(() => {
        if (currentPriceBlock) {
          currentPriceBlock.classList.remove('price--loading');
          currentPriceBlock.querySelector('.price')?.classList.remove('price--loading');
        }
        this.#removeInitialLoadingClass();
      });
  }

  /**
   * Section id for the main product section (for Section Rendering API). From variant-selects or product-info only.
   * @returns {string | null}
   */
  #getMainProductSectionId() {
    const variantSelects = document.querySelector(this.selectors.variantSelectsSection);
    const productInfo = document.querySelector(this.selectors.productInfoSection);
    const raw = variantSelects?.getAttribute(this.selectors.attributeDataSection) || productInfo?.getAttribute(this.selectors.attributeDataSection) || '';
    const id = String(raw).trim();
    if (!id || id.includes('predictive-search') || id.includes('header') || id === 'empty') return null;
    return id;
  }

  #removeInitialLoadingClass() {
    const productInfo = this.closest(this.selectors.productInfoSection) || document.querySelector(this.selectors.productInfoSection);
    if (productInfo) {
      productInfo.classList.remove('location-pricing-loading');
    }
    const currentPriceBlock = document.querySelector(this.selectors.priceBlock) || document.querySelector(this.selectors.priceGroup);
    if (currentPriceBlock) {
      currentPriceBlock.classList.remove('price--loading');
      currentPriceBlock.querySelector('.price')?.classList.remove('price--loading');
    }
  }

  /**
   * Public helper: get current pricebook value.
   * @returns {string | null}
   */
  getCurrentPricebook() {
    return this.#currentPricebook;
  }
}

if (!customElements.get('location-variant-selector')) {
  customElements.define('location-variant-selector', LocationVariantSelector);
}

export default LocationVariantSelector;

